import {
  Button,
  Icon,
  IconType,
  TabbedDrawer,
  TabbedDrawerHeader,
  TabbedDrawerTitle,
  TabbedDrawerTab,
  TabbedDrawerCTA,
} from "@sellar-io/components";
import type { ReactNode } from "react";
import * as ROUTES from "../../constants/routes";
import {
  capitalize,
  formatCurrency,
  generateContainerTypeDeliveryChargeRulesText,
  generateFulfillmentTypeContainerDeliveryChargeName,
  getDisplayNameByKey,
  orderableItemValues,
  pluralize,
} from "@sellar-io/common";
import { FulfillmentTypeListResponseType } from "@sellar-io/api-v2/types";
import { generatePriceTierOrderRuleDescription } from "../../utils/sellarHelpers";
import { useNavigate } from "react-router";

type DeliveryMethodPreviewSlideOutProps = {
  active: boolean;
  setActive: (value: boolean) => void;
  deliveryMethod?: FulfillmentTypeListResponseType[number];
  deliveryZoneId?: number;
  assign: () => void;
};

export const DeliveryMethodPreviewSlideOut = ({
  active,
  setActive,
  deliveryMethod,
  deliveryZoneId,
  assign,
}: DeliveryMethodPreviewSlideOutProps) => {
  return (
    <TabbedDrawer active={active} setActive={setActive} initTab={0}>
      {active && deliveryMethod && (
        <Content
          deliveryMethod={deliveryMethod}
          deliveryZoneId={deliveryZoneId}
          assign={assign}
        />
      )}
    </TabbedDrawer>
  );
};

const Content = ({
  deliveryMethod,
  deliveryZoneId,
  assign,
}: {
  deliveryMethod: FulfillmentTypeListResponseType[number];
  deliveryZoneId?: number;
  assign: () => void;
}) => {
  const navigate = useNavigate();

  const hasOrderRules =
    deliveryMethod.OrderRules &&
    (deliveryMethod.OrderRules.PriceTierOrderRules.length > 0 ||
      deliveryMethod.OrderRules.minimumOrderPrice !== null);

  const hasDeliveryOffers =
    deliveryMethod.ContainerTypeDeliveryChargeRules.length > 0 ||
    deliveryMethod.freeDeliveryBasketSubtotal !== null;

  const hasContainerRules =
    deliveryMethod.DeliveryMethodContainerRules.length > 0;
  const numberOfDeliveryZones = 0;

  return (
    <>
      <TabbedDrawerHeader>
        <TabbedDrawerTitle
          title={
            deliveryMethod.name ?? capitalize(deliveryMethod.fulfillmentType)
          }
          subtitle={`Assigned to ${numberOfDeliveryZones} ${pluralize(
            "delivery zone",
            numberOfDeliveryZones,
          )}`}
          customIcon={
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-zinc-200 text-zinc-800">
              <Icon icon="TRUCK_OUTLINE" size={1.2} />
            </div>
          }
        />
      </TabbedDrawerHeader>

      <TabbedDrawerTab tab={0}>
        <div className="flex flex-col gap-3 px-4 pt-4">
          <PreviewCard title="Container rules">
            <p className="text-sm text-zinc-700">
              {hasContainerRules
                ? `${deliveryMethod.DeliveryMethodContainerRules.map(
                    (rule) =>
                      `${rule.volume}${rule.unit} ${getDisplayNameByKey(
                        orderableItemValues,
                        rule.containerType,
                      )}`,
                  ).join(", ")} not available`
                : "All container types available"}
            </p>
          </PreviewCard>

          <PreviewCard
            title="Order requirements"
            labelClassName="bg-indigo-100 text-indigo-800"
            icon="CLIPBOARD_CHECK_OUTLINE"
          >
            <div className="flex flex-col gap-1 text-sm text-zinc-700">
              {deliveryMethod.OrderRules?.minimumOrderPrice ? (
                <p>
                  Minimum order total of{" "}
                  {formatCurrency(deliveryMethod.OrderRules.minimumOrderPrice)}
                </p>
              ) : null}
              {deliveryMethod.OrderRules?.PriceTierOrderRules.map((rule) => (
                <p key={rule.id}>
                  {generatePriceTierOrderRuleDescription(rule)}
                </p>
              ))}
              {!hasOrderRules && <p>No order requirements</p>}
            </div>
          </PreviewCard>

          <PreviewCard
            title="Delivery charges"
            labelClassName="bg-zinc-100 text-zinc-800"
            icon="TRUCK"
          >
            <div className="flex flex-col gap-1 text-sm text-zinc-700">
              <p>
                {deliveryMethod.charge
                  ? `${formatCurrency(deliveryMethod.charge)} flat delivery charge`
                  : "No flat delivery charge"}
              </p>
              {deliveryMethod.FulfillmentTypeContainerDeliveryCharges.length >
                0 && (
                <>
                  <p className="font-semibold text-zinc-900">
                    Per-item charges
                  </p>
                  {deliveryMethod.FulfillmentTypeContainerDeliveryCharges.map(
                    (charge) => (
                      <div
                        key={charge.id}
                        className="flex items-center justify-between gap-4"
                      >
                        <span>
                          {generateFulfillmentTypeContainerDeliveryChargeName({
                            volume: charge.volume,
                            packQuantity: charge.packQuantity,
                            displayUnit: charge.displayUnit,
                            containerType: charge.containerType,
                          })}
                        </span>
                        <span>{formatCurrency(charge.charge)}</span>
                      </div>
                    ),
                  )}
                </>
              )}
            </div>
          </PreviewCard>

          <PreviewCard
            title="Delivery offers"
            labelClassName="bg-emerald-100 text-emerald-800"
            icon="TRUCK"
          >
            <div className="flex flex-col gap-1 text-sm text-zinc-700">
              {deliveryMethod.freeDeliveryBasketSubtotal ? (
                <p>
                  Free delivery over{" "}
                  {formatCurrency(deliveryMethod.freeDeliveryBasketSubtotal)}
                </p>
              ) : null}
              {deliveryMethod.ContainerTypeDeliveryChargeRules.map((rule) => (
                <p key={rule.id}>
                  {generateContainerTypeDeliveryChargeRulesText(rule)}
                </p>
              ))}
              {!hasDeliveryOffers && <p>No free delivery offers</p>}
            </div>
          </PreviewCard>
        </div>
      </TabbedDrawerTab>

      <TabbedDrawerCTA className="flex-row">
        <Button
          className="flex w-full items-center gap-1"
          color="transparent"
          onClick={() =>
            navigate(
              ROUTES.DELIVERY_METHOD.href({
                deliveryMethodId: deliveryMethod.id,
              }),
              { state: { deliveryZoneRedirectId: deliveryZoneId } },
            )
          }
        >
          <Icon icon="PENCIL" />
          Edit
        </Button>
        <Button className="w-full" onClick={assign}>
          Assign
        </Button>
      </TabbedDrawerCTA>
    </>
  );
};

const PreviewCard = ({
  title,
  icon,
  labelClassName = "",
  children,
}: {
  title: string;
  icon?: IconType;
  labelClassName?: string;
  children: ReactNode;
}) => {
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-3">
      <div className="mb-2 flex items-center gap-1.5">
        {icon ? (
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-sm font-medium ${labelClassName}`}
          >
            <Icon icon={icon} size={0.65} />
            {title}
          </span>
        ) : (
          <h3 className="font-semibold text-zinc-900">{title}</h3>
        )}
      </div>
      {children}
    </div>
  );
};
